import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Typography, Spin, Input, Row, Col, DatePicker } from 'antd';
import axiosInstance from '../../../axiosInstance';

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

const shopId = localStorage.getItem('shopId');

const Products = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [adjustments, setAdjustments] = useState([]);
    const [adjustmentsModalVisible, setAdjustmentsModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [dateRange, setDateRange] = useState([]);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/products');
            const productData = response.data.products || [];
            setProducts(productData);
            setFilteredProducts(productData);
        } catch (error) {
            message.error('Failed to fetch products.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAdjustments = async (productId, productName) => {
        if (!shopId || !productId) {
            message.error('Missing shop or product information.');
            return;
        }
        setLoading(true);
        setSelectedProduct(productName);
        try {
            const response = await axiosInstance.get(`/shops/${shopId}/products/${productId}/adjustments`);
            setAdjustments(response.data.adjustments || []);
            setAdjustmentsModalVisible(true);
        } catch (error) {
            message.error('Error fetching product adjustments.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        if (value) {
            const filtered = products.filter(
                (product) =>
                    product.name.toLowerCase().includes(value.toLowerCase()) ||
                    product.category?.name?.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredProducts(filtered);
        } else {
            setFilteredProducts(products);
        }
    };

    const handleDateFilter = (dates) => {
        if (dates && dates.length === 2) {
            setDateRange(dates);
        } else {
            setDateRange([]);
        }
    };

    const filteredAdjustments = dateRange.length === 2
        ? adjustments.filter(adj => {
            const adjDate = new Date(adj.created_at);
            return adjDate >= dateRange[0].toDate() && adjDate <= dateRange[1].toDate();
        })
        : adjustments;

    const adjustmentsColumns = [
        {
            title: 'Adjustment Number',
            dataIndex: 'adj_number',
            key: 'adj_number',
        },
        {
            title: 'Type of Adjustment',
            key: 'adjustment_type',
            render: (record) => record.items.map(item => (
                <div key={item.id}>{item.type}</div>
            )),
        },
        {
            title: 'Previous Quantity',
            key: 'previous_quantity',
            render: (record) => record.items.map(item => (
                <div key={item.id}>{item.previous_quantity !== null ? item.previous_quantity : '-'}</div>
            )),
        },
        {
            title: 'Quantity Adjusted',
            dataIndex: 'items',
            key: 'quantity_adjusted',
            render: (items) => items.reduce((total, item) => total + item.quantity, 0),
        },
        {
            title: 'Remaining Quantity',
            key: 'remaining_quantity',
            render: (record) => record.items.map(item => (
                <div key={item.id}>{item.remaining_quantity !== null ? item.remaining_quantity : '-'}</div>
            )),
        },
        {
            title: 'Adjustment Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString(),
        },
    ];

    return (
        <div style={{ padding: '20px', maxWidth: '100%' }}>
            <Row justify="space-between" style={{ marginBottom: '20px' }}>
                <Col span={12}>
                    <Search
                        placeholder="Search products or categories"
                        onSearch={handleSearch}
                        enterButton
                        allowClear
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </Col>
            </Row>

            <Table
                columns={[
                    {
                        title: 'Product Name',
                        dataIndex: 'name',
                        key: 'name',
                        sorter: (a, b) => a.name.localeCompare(b.name),
                    },
                    {
                        title: 'Category',
                        dataIndex: ['category', 'name'],
                        key: 'category',
                    },
                    {
                        title: 'Action',
                        key: 'action',
                        render: (_, product) => (
                            <Button type="link" onClick={() => fetchAdjustments(product.id, product.name)}>
                                View Adjustments
                            </Button>
                        ),
                    },
                ]}
                dataSource={filteredProducts}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
            />

            <Modal
                title={`Adjustments for ${selectedProduct}`}
                visible={adjustmentsModalVisible}
                onCancel={() => setAdjustmentsModalVisible(false)}
                footer={null}
                width="80%"
                destroyOnClose
            >
                <Row style={{ marginBottom: '10px' }}>
                    <Col span={12}>
                        <RangePicker onChange={handleDateFilter} />
                    </Col>
                </Row>
                {loading ? (
                    <Spin size="large" />
                ) : (
                    <Table
                        columns={adjustmentsColumns}
                        dataSource={filteredAdjustments}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        bordered
                        size="small"
                    />
                )}
            </Modal>
        </div>
    );
};

export default Products;
